
'use client';

import { Button } from '@/components/ui/button';
import { ArrowRight, Menu, MoveUpRight, X } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

export function AnimatedInteractiveHomepage() {
  const [mousePosition, setMousePosition] = useState({ x: -1000, y: -1000 });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        setMousePosition({ x: event.clientX - rect.left, y: event.clientY - rect.top });
      }
    };
    
    const container = containerRef.current;
    container?.addEventListener('mousemove', handleMouseMove);

    const handleMouseLeave = () => {
      setMousePosition({ x: -1000, y: -1000 });
    }
    container?.addEventListener('mouseleave', handleMouseLeave);


    return () => {
      container?.removeEventListener('mousemove', handleMouseMove);
      container?.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <div ref={containerRef} className="bg-gray-900 text-white min-h-screen font-sans relative overflow-hidden">
      <style jsx>{`
        .gradient-spotlight {
          background: radial-gradient(
            circle 600px at ${mousePosition.x}px ${mousePosition.y}px,
            rgba(29, 78, 216, 0.15),
            transparent 80%
          );
        }
        .card-hover-glow {
          background: radial-gradient(
            circle 400px at var(--mouse-x) var(--mouse-y),
            rgba(255, 255, 255, 0.1),
            transparent 60%
          );
          border: 1px solid transparent;
          transition: border-color 0.3s ease;
        }
        .card-hover-glow:hover {
          border-color: rgba(255, 255, 255, 0.2);
        }
      `}</style>
      <div className="gradient-spotlight pointer-events-none absolute inset-0 z-0"></div>
      <header className="relative z-20 p-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold tracking-tighter">Flux</h2>
        <nav className="hidden md:flex items-center gap-6 text-sm">
          <a
            href="#"
            className="opacity-70 hover:opacity-100 transition-opacity"
          >
            Work
          </a>
          <a
            href="#"
            className="opacity-70 hover:opacity-100 transition-opacity"
          >
            Studio
          </a>
          <a
            href="#"
            className="opacity-70 hover:opacity-100 transition-opacity"
          >
            Careers
          </a>
          <Button
            variant="outline"
            className="text-white border-white/20 hover:bg-white/10 hover:text-white rounded-full"
          >
            Contact Us
          </Button>
        </nav>
        <div className="md:hidden">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X /> : <Menu />}
          </Button>
        </div>
      </header>

      {isMenuOpen && (
        <div className="relative z-20 md:hidden bg-gray-900/80 backdrop-blur-sm">
          <nav className="flex flex-col items-center gap-6 text-lg py-8">
            <a href="#" className="hover:opacity-100 transition-opacity">
              Work
            </a>
            <a href="#" className="hover:opacity-100 transition-opacity">
              Studio
            </a>
            <a href="#" className="hover:opacity-100 transition-opacity">
              Careers
            </a>
            <Button
              variant="outline"
              className="text-white border-white/20 hover:bg-white/10 hover:text-white rounded-full"
            >
              Contact Us
            </Button>
          </nav>
        </div>
      )}

      <main className="relative z-10">
        <section className="min-h-[80vh] flex items-center justify-center text-center px-4">
          <div>
            <h1 className="text-5xl md:text-8xl font-bold tracking-tighter bg-clip-text text-transparent bg-gradient-to-b from-white to-gray-400">
              Future Forward
            </h1>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-gray-300">
              We design and build dynamic digital experiences that captivate
              and convert.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
              <Button
                size="lg"
                className="rounded-full bg-white text-black hover:bg-gray-200"
              >
                See Our Work <ArrowRight className="ml-2" />
              </Button>
            </div>
          </div>
        </section>

        <section className="py-20 md:py-32 container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold tracking-tighter">What We Do</h2>
            <p className="mt-2 text-gray-400 max-w-xl mx-auto">
              From concept to launch, we bring your vision to life with
              cutting-edge technology and design.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Interactive Web Design',
                description:
                  'Engaging websites that tell a story and invite exploration.',
              },
              {
                title: 'Mobile App Development',
                description:
                  'Intuitive and performant applications for iOS and Android.',
              },
              {
                title: 'Generative AI Integration',
                description:
                  'Leveraging AI to create unique, dynamic content and user experiences.',
              },
            ].map((service) => (
              <div
                key={service.title}
                className="card-hover-glow p-8 rounded-2xl relative"
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  e.currentTarget.style.setProperty(
                    '--mouse-x',
                    `${e.clientX - rect.left}px`
                  );
                  e.currentTarget.style.setProperty(
                    '--mouse-y',
                    `${e.clientY - rect.top}px`
                  );
                }}
              >
                <div className="absolute top-8 right-8 w-12 h-12 bg-blue-500/20 rounded-full blur-2xl"></div>
                <h3 className="text-2xl font-bold">{service.title}</h3>
                <p className="mt-2 text-gray-400">{service.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-20 md:py-32 container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold tracking-tighter">
              Featured Projects
            </h2>
          </div>
          <div className="space-y-4">
            {['Project Nova', 'Echo Systems', 'Quantum Leap'].map((project) => (
              <a
                href="#"
                key={project}
                className="group block p-6 border-b border-gray-800 hover:bg-gray-800/50 rounded-lg transition-colors"
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-3xl md:text-4xl font-bold tracking-tight">
                    {project}
                  </h3>
                  <MoveUpRight className="w-8 h-8 text-gray-500 group-hover:text-white transition-colors transform group-hover:rotate-45" />
                </div>
              </a>
            ))}
          </div>
        </section>
      </main>
      <footer className="relative z-10 py-12 border-t border-gray-800">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-center text-sm text-gray-500 text-center gap-4 px-4">
          <p>&copy; {new Date().getFullYear()} Flux. All Rights Reserved.</p>
          <div className="flex gap-4">
            <a href="#" className="hover:text-white">
              Twitter
            </a>
            <a href="#" className="hover:text-white">
              LinkedIn
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
